import { HotelRatingSquare, HotelStars, TourQuantity } from '@lt/components';
import { LabelList } from '@lt/components/components/Label/LabelList';
import { isEmpty } from 'lodash';
import React, { lazy, Suspense, useEffect, useState } from 'react';
import styled from 'styled-components';
import ErrorBoundary from 'src/error/components/ErrorBoundary';

import { useTranslation } from '@lt/localization';

import type { TLabelStyle } from '@lt/components/components/Label/Label.model';
import Notices from 'checkout/components/Hotel/Notices';
import TourGuide from 'src/components/Banners/TourGuide';
import ToggleMapButtonMobile from 'src/routes/Checkout/components/ToggleMapButtonMobile';
import Wishlist from 'src/components/Wishlist';
import LazyYandexMapsCheckout from 'src/routes/Checkout/components/YandexMapsCheckout/LazyYandexMapsCheckout';
import HotelMapLoader from 'src/components/YandexMap/components/HotelMapLoader';
import PromoBanner from 'src/components/Banners/PromoBanner';
import Cookies from 'js-cookie';
import { KEY_COOKIE } from 'src/components/Banners/TourGuide/constants';
import { BannerSwiper } from 'src/components/Banners/BannerSwiper/BannerSwiper';
import { KEY_PROMO_COOKIE } from 'src/components/Banners/PromoBanner/constants';
import { useGetFlagSigmaOptionByName } from 'src/utils/featureToggleSigma/hooks/useGetFlagSigmaOptionByName';
import { HotelGallery } from '.';
import Metapolicies from '../../../../../../../components/Metapolicies';

import {
  BillInfo,
  LTExtrasContainer,
  ShortInfo,
} from '../../../containers/Package';

import CancellationPolicyContainer from '../../../../../components/CancellationPolicyContainer';
import GoToHotelContainer from '../../../../../components/GoToHotel/GoToHotelContainer';
import OperatorInfoContainer from '../../../../../components/OperatorInfo';
import PackageId from '../../../../../components/PackageId';
import PackageInfo from '../../../../../components/PackageInfo';
import GoToFlightButtonMobile from './GoToFlightsButtonMobile';

// Constants
import { CANCELLATION_POLICY_TOOLTIP_TEXT } from '../constants';

import type { IHotel } from '../../../../../types/hotelTypes';

import HotelRoulette from './HotelRoulette';
import VisaInfoContainer from '../../../../../components/VisaContainer';

import {
  Experiment,
  EXPERIMENT_NAMES,
  Variant,
} from '../../../../../../../components/Experiment';

const EmptyMap = lazy(
  () =>
    import(
      /* webpackChunkName: "EmptyMap" */ 'src/routes/Hotel/components/EmptyBlock'
    ),
);

interface HotelProps extends IHotel {
  isWhiteLabel: boolean;
}

const Hotel = (props: HotelProps) => {
  const {
    name,
    price,
    stars,
    location,
    images,
    rating,
    coordinates,
    isMapExpanded = false,
    toggleMap,
    notices = [],
    isWebGlSupported,
    isManualPackage,
    hasAdjustParams,
    isRoulette,
    isMobile,
    availability,
    labels,
    hotelMetapolicies,
    cancellationPolicy,
    archivedAt,
    isFetching,
    packageId,
    isHotelOnly,
    isWhiteLabel,
  } = props;

  const LABEL_STYLE: TLabelStyle = 'LABEL';
  const styledLabels = labels.map((label) => ({
    ...label,
    style: LABEL_STYLE,
  }));

  const { t } = useTranslation();

  const hasMetapolicies = !isEmpty(hotelMetapolicies);

  const showMap =
    isWebGlSupported && !isRoulette && (!isMobile || isMapExpanded);
  const showGoToFlights = isMobile && !isManualPackage && !hasAdjustParams;

  const hasFlexibleCancellationPolicy = cancellationPolicy?.kind === 'flexible';

  const showFullscreenControl = !isMobile;

  const { value } = useGetFlagSigmaOptionByName('promo_banner_enabled');
  const promoBannerEnabled = !!value && !isWhiteLabel;

  // Проверяем, активен ли TourGuide
  const [showTourGuide, setShowTourGuide] = useState(() => {
    const cookieValue = Cookies.get(KEY_COOKIE);
    return cookieValue ? Boolean(JSON.parse(cookieValue)) : true;
  });

  // Проверяем, активен ли PromoBanner
  const [showPromoBanner, setShowPromoBanner] = useState(() => {
    const cookieValue = Cookies.get(KEY_PROMO_COOKIE);
    return cookieValue ? Boolean(JSON.parse(cookieValue)) : true;
  });

  useEffect(() => {
    const handleTourGuideCookieChange = (event: Event) => {
      const { value: tourGuideValue } = (event as CustomEvent).detail;
      setShowTourGuide(tourGuideValue);
    };

    const handlePromoBannerCookieChange = (event: Event) => {
      const { value: promoBannerValue } = (event as CustomEvent).detail;
      setShowPromoBanner(promoBannerValue);
    };

    window.addEventListener(
      'tourGuideCookieChange',
      handleTourGuideCookieChange,
    );
    window.addEventListener(
      'promoBannerCookieChange',
      handlePromoBannerCookieChange,
    );

    return () => {
      window.removeEventListener(
        'tourGuideCookieChange',
        handleTourGuideCookieChange,
      );
      window.removeEventListener(
        'promoBannerCookieChange',
        handlePromoBannerCookieChange,
      );
    };
  }, []);

  const showBannerSwiper =
    isMobile && promoBannerEnabled && showPromoBanner && showTourGuide;

  return (
    <Container $isMobile={isMobile}>
      <aside>
        <Header>
          <HotelStarsContainer>
            <PackageId />
            <HotelStars value={stars} />
            <Location>{location}</Location>
          </HotelStarsContainer>
          {isMobile && (
            <div className="flex flex-column flex-align-end">
              <Wishlist
                price={price || 0}
                wishlistIdentifier={packageId}
                type="package"
                forceDesktop
              />
              {!archivedAt && (
                <Experiment name={EXPERIMENT_NAMES.ACTUALIZE_ALTERNATIVES.NAME}>
                  <Variant
                    option={EXPERIMENT_NAMES.ACTUALIZE_ALTERNATIVES.OPTIONS.OFF}
                  >
                    <OperatorInfoContainer className="checkout-shortinfo__top-operator_text" />
                  </Variant>
                </Experiment>
              )}
            </div>
          )}
        </Header>
        <Title>
          <span>{`${t('checkout.hotel.title')} `}</span>
          <GoToHotelContainer title={t('checkout.hotel.link')}>
            {name}
          </GoToHotelContainer>
        </Title>
        <StyledVisaInfoContainer />

        {availability && (
          <TourQuantity
            position={isMobile}
            text={availability.tooltip}
            availability={availability}
            withTooltip={false}
            isMobile={isMobile}
            marginBottom={isMobile ? 8 : 16}
            isPackageCheckout
          />
        )}

        {!isEmpty(notices) && <Notices marginTop={0} notices={notices} />}

        <MediaContainer $isMobile={isMobile}>
          {!isMapExpanded && (
            <div>
              <RatingContainer>
                <HotelRatingSquare value={rating} count={rating} />
              </RatingContainer>
              {isRoulette ? (
                <HotelRoulette />
              ) : (
                <HotelGallery images={images} isMobile={isMobile} />
              )}
              {isMobile && !isRoulette && (
                <ToggleMapButton toggleHandler={toggleMap} mapView={false} />
              )}
            </div>
          )}
          {showMap && (
            <ErrorBoundary
              fallback={<EmptyMap isMobile={isMobile} />}
              showDialog={false}
            >
              <Suspense fallback={<HotelMapLoader />}>
                <StyledHotelMap
                  $isArchived={Boolean(archivedAt)}
                  $isMobile={isMobile}
                >
                  <LazyYandexMapsCheckout
                    isMobile={isMobile}
                    location={location}
                    coordinates={coordinates}
                    onExpand={toggleMap}
                    showFullscreenControl={showFullscreenControl}
                    isExpanded={isMapExpanded}
                    isNoTopBorderRadius={!isMobile}
                  />
                </StyledHotelMap>
              </Suspense>
            </ErrorBoundary>
          )}
        </MediaContainer>

        {!isMobile ? (
          <>
            <StyledLabelList labels={styledLabels} $isMobile={isMobile} />
            {hasMetapolicies ? (
              <Metapolicies
                metapolicies={hotelMetapolicies}
                showLabels={false}
              />
            ) : null}
          </>
        ) : null}
      </aside>
      <section>
        <ShortInfo isArchived={Boolean(archivedAt)} />
        <BillInfo isArchived={Boolean(archivedAt)} />
        {isMobile ? (
          <StyledLabelList labels={styledLabels} $isMobile={isMobile} />
        ) : null}
        <PackageInfo isArchived={Boolean(archivedAt)} />
        {!hasAdjustParams && <LTExtrasContainer />}
        {hasFlexibleCancellationPolicy ? (
          <StyledCancellaionPolicyBlock $isMobile={isMobile}>
            <StyledCancellationPolicyHeader
              $isMobile={isMobile}
              $hasExtras={!hasAdjustParams}
            >
              {t('checkout.hotel.cancellation_package')}
            </StyledCancellationPolicyHeader>
            <CancellationPolicyContainer
              showBackground={false}
              showPopup
              title={t('checkout.hotel.cancellation_package')}
              tooltipText={CANCELLATION_POLICY_TOOLTIP_TEXT}
            />
          </StyledCancellaionPolicyBlock>
        ) : null}
        {!archivedAt && showGoToFlights && <GoToFlightButtonMobile />}
        {isMobile && hasMetapolicies ? (
          <Metapolicies
            metapolicies={hotelMetapolicies}
            isMobile
            showLabels={false}
          />
        ) : null}

        {!isManualPackage && !isFetching && (
          <>
            {showBannerSwiper ? (
              <BannerSwiper>
                <PromoBanner
                  isMobile={isMobile}
                  showTourGuide={showTourGuide}
                />
                <TourGuide
                  isHotelOnly={isHotelOnly}
                  isMobile={isMobile}
                  isCheckoutPage
                />
              </BannerSwiper>
            ) : (
              <BannersContainer $isMobile={isMobile}>
                {promoBannerEnabled && (
                  <PromoBanner
                    isMobile={isMobile}
                    showTourGuide={showTourGuide}
                  />
                )}
                <TourGuide
                  isHotelOnly={isHotelOnly}
                  isMobile={isMobile}
                  isCheckoutPage
                />
              </BannersContainer>
            )}
          </>
        )}
      </section>
    </Container>
  );
};

const Container = styled.div<{
  $isMobile: boolean;
}>`
  ${({ $isMobile }) =>
    $isMobile
      ? `
      margin: 0 5px 8px;
      padding: 10px;
      display: flex;
      flex-direction: column;
      section {
        margin-top: 5px;
      }
      aside {
        margin-right: 0;
        display: block; //ios10
      }
      `
      : `
    margin-bottom: 20px;
    padding: 15px 25px 20px 10px;
    display: flex;
    aside {
      margin-right: 25px;
      display: flex;
      width: 100%;
      flex-direction: column;
      flex-grow: 1;
    }
    section {
      flex-shrink: 0;
      flex-basis: 52%;
    }
  `}
  background-color: var(--white);
  border-radius: var(--border-radius);
`;

const Header = styled.div`
  display: flex;
  position: relative;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 13px;
`;

const Title = styled.div`
  margin-bottom: 2px;
  color: var(--black);
  font-size: 22px;
  font-style: normal;
  font-weight: 500;

  a {
    color: var(--black);
    text-decoration: none;
  }
`;

const StyledVisaInfoContainer = styled(VisaInfoContainer)`
  margin-bottom: 2px;
`;

const BannersContainer = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: row;
  gap: 12px;
  margin-top: ${({ $isMobile }) => ($isMobile ? '12px' : '16px')};
  margin-bottom: ${({ $isMobile }) => ($isMobile ? '4px' : '0')};

  > * {
    flex: 1;
    min-width: 0;
  }
`;

const MediaContainer = styled.div<{ $isMobile: boolean }>`
  ${({ $isMobile }) =>
    $isMobile
      ? `
    height: 300px;
    min-height: 300px;
    `
      : `
    height: 100%;
    min-height: 400px;
    .carousel .slide {
      background-color: #f3f3f3;
    }
  `}
  position: relative;
  display: flex;
  overflow: hidden;
  flex-direction: column;
  border-radius: 4px;
`;

const RatingContainer = styled.div`
  position: absolute;
  top: 10px;
  right: 0;
  margin-right: 10px;
  display: flex;
  z-index: 1;
`;

const Location = styled.div`
  color: var(--black);
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
`;

const HotelStarsContainer = styled.div`
  display: flex;
  gap: 8px;
`;

const StyledLabelList = styled(LabelList)<{ $isMobile: boolean }>`
  margin-top: ${({ $isMobile }) => ($isMobile ? 4 : 8)}px;
`;

const StyledCancellationPolicyHeader = styled.span<{
  $isMobile: boolean;
  $hasExtras: boolean;
}>`
  padding: ${({ $isMobile }) =>
    $isMobile ? '16px 0px 3px 0px' : '16px 0px 0px 16px'};
  ${({ $isMobile, $hasExtras }) =>
    $hasExtras && $isMobile && 'padding-top: 3px;'}
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #17181a;
  margin-bottom: 8px;
`;

const StyledCancellaionPolicyBlock = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  ${({ $isMobile }) => $isMobile && 'margin-bottom: 16px;'}

  > div {
    ${({ $isMobile }) => !$isMobile && `margin-left: 12px;`}
  }
`;

const StyledHotelMap = styled.div<{ $isArchived: boolean; $isMobile: boolean }>`
  display: flex;
  flex-grow: 1;

  canvas {
    ${({ $isArchived }) => $isArchived && 'filter: grayscale(1);'}
  }
`;

const ToggleMapButton = styled(ToggleMapButtonMobile)`
  position: absolute;
  right: 10px;
  bottom: 10px;
`;

export default Hotel;
